import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    UpdateAdyenPaymentModuleDocument,
    type UpdateAdyenPaymentModuleMutation,
    type UpdateAdyenPaymentModuleMutationVariables,
} from '../../../../api/mutations/updateAdyenPaymentModule';
import { type AdyenPaymentModuleSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = AdyenPaymentModuleSettings;

type UpdateAdyenPaymentModuleFormProps = {
    moduleId: string;
    initialValues: FormValues;
    onComplete: () => void;
};

const loadingMessageKey = 'update-adyen-payment-module-loading';

const UpdateAdyenPaymentModuleForm = ({ moduleId, initialValues, onComplete }: UpdateAdyenPaymentModuleFormProps) => {
    const { t } = useTranslation(['adyenPaymentModuleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('adyenPaymentModuleDetails:paymentModuleDrawer.messages.submitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateAdyenPaymentModuleMutation, UpdateAdyenPaymentModuleMutationVariables>({
                    mutation: UpdateAdyenPaymentModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('adyenPaymentModuleDetails:paymentModuleDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="updateAdyenPaymentModule"
                    layout="vertical"
                    name="updateAdyenPaymentModule"
                    onSubmitCapture={handleSubmit}
                >
                    <InputField
                        {...t('adyenPaymentModuleDetails:paymentModuleDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default UpdateAdyenPaymentModuleForm;
